// src/components/BotSettings/SellStrategy.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, AlertCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

export const SellStrategy: React.FC<any> = ({
  shortEnabled = false, onShortEnabledChange = () => {},
  sellOrderType = '', onSellOrderTypeChange = () => {},
  maxOpenOrders = '', onMaxOpenOrdersChange = () => {},
  onlyOneOpenSellOrder = false, onOnlyOneOpenSellOrderChange = () => {},
  onlyNegativePairs = false, onOnlyNegativePairsChange = () => {},
  sellPercentRange = '', onSellPercentRangeChange = () => {},
  sellCooldownTime = '', onSellCooldownTimeChange = () => {}
}) => {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-8">
      <div className={cn("bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 relative", !shortEnabled && "opacity-60")}>
        
        {!shortEnabled && (
          <div className="absolute inset-0 z-10 bg-gray-50/50 dark:bg-gray-900/60 backdrop-blur-[2px] flex items-center justify-center rounded-xl pointer-events-none">
             <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 flex items-center gap-3">
               <AlertCircle className="h-6 w-6 text-gray-400" />
               <div>
                 <p className="font-bold text-gray-900 dark:text-white">Short Strategy is Disabled</p>
                 <p className="text-sm text-gray-500 dark:text-gray-400">Turn on the toggle below to configure.</p>
               </div>
             </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Short Entry Strategy</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Configure short position entry strategy</p>
          </div>
          <div className="h-12 w-12 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
            <TrendingDown className="h-6 w-6 text-red-500" />
          </div>
        </div>

        <div className="p-4 bg-red-50 dark:bg-red-900/10 rounded-lg border-2 border-red-200 dark:border-red-800 mb-6 relative z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
              <label className="text-sm font-semibold text-gray-900 dark:text-white block">Enable SHORT (Sell)</label>
            </div>
            <button onClick={() => onShortEnabledChange(!shortEnabled)} className={cn('relative inline-flex h-7 w-14 items-center rounded-full transition-colors pointer-events-auto', shortEnabled ? 'bg-red-600' : 'bg-gray-300 dark:bg-gray-600')}>
              <span className={cn('inline-block h-5 w-5 transform rounded-full bg-white transition-transform', shortEnabled ? 'translate-x-8' : 'translate-x-1')} />
            </button>
          </div>
        </div>

        <div className={cn("space-y-6 transition-opacity duration-300", !shortEnabled && "opacity-40 pointer-events-none grayscale")}>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Order Type</label>
            <select value={sellOrderType || ''} onChange={(e) => onSellOrderTypeChange(e.target.value === '' ? null : e.target.value)} className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 pointer-events-auto">
              <option value="">Select Order Type</option>
              <option value="market">Market Order</option>
              <option value="limit">Limit Order</option>
            </select>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Maximum Open Orders</label>
            {/* 🔥 APSAUGA NUO 400 KLAIDOS */}
            <input type="number" min="1" value={maxOpenOrders ?? ''} onChange={(e) => onMaxOpenOrdersChange(e.target.value === '' ? null : Number(e.target.value))} className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 pointer-events-auto" />
          </div>

          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer pointer-events-auto">
              <input type="checkbox" className="sr-only peer" checked={onlyOneOpenSellOrder === true} onChange={(e) => onOnlyOneOpenSellOrderChange(e.target.checked)} />
              <div className={cn("w-11 h-6 rounded-full peer bg-gray-200 dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600")}></div>
            </label>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Limit to One Sell Order per Cryptocurrency</span>
          </div>

          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer pointer-events-auto">
              <input type="checkbox" className="sr-only peer" checked={onlyNegativePairs === true} onChange={(e) => onOnlyNegativePairsChange(e.target.checked)} />
              <div className={cn("w-11 h-6 rounded-full peer bg-gray-200 dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600")}></div>
            </label>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Only Negative Pairs</span>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Percent Range (%)</label>
            <input type="number" step="0.01" value={sellPercentRange ?? ''} onChange={(e) => onSellPercentRangeChange(e.target.value === '' ? null : Number(e.target.value))} className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 pointer-events-auto" />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Cooldown Time (Minutes)</label>
            <input type="number" min="0" value={sellCooldownTime ?? ''} onChange={(e) => onSellCooldownTimeChange(e.target.value === '' ? null : Number(e.target.value))} className="w-full px-4 py-2 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 pointer-events-auto" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};